import { HttpClient } from '@angular/common/http';
import { Component, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { environment } from 'environments/environment';

import { SystemMessage } from 'app/core/system-message/system-message-service';
import { NotificationService } from 'app/notification.service';
import { RecaptchaService } from 'app/services/recaptcha.service';
import { PcgSelectComponent } from 'app/shared/form-elements/components/pcg-select-component/select.component';
import { validateForm } from 'app/shared/form-elements/form-validateForm.function';
import { PfsHelpTicketVm } from 'app/shared/generated/Models/Public/PfsHelpTicketVm';
import { pcgSettings } from 'app/shared/generated/pcg-settings';
import { SelectItem } from 'app/shared/models/select-item';

@Component({
    selector: 'pcg-public-help-ticket',
    templateUrl: './help-ticket.component.html',
    styleUrls: ['./help-ticket.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class PublicHelpTicketComponent {
    @ViewChild('facilitySelect', { static: false }) facilitySelect: PcgSelectComponent;
    @ViewChild('categorySelect', { static: false }) categorySelect: PcgSelectComponent;
    @ViewChild('prioritySelect', { static: false }) prioritySelect: PcgSelectComponent;

    formGroup = PfsHelpTicketVm.Form;
    model = PfsHelpTicketVm.GetModel(this.formGroup);
    categories: SelectItem[] = [];

    isSuccess = false;
    isRegister = false;

    returnUrl = '';
    facilityName = '';
    categoryName = 'Login Issues';
    priorityName = 'High';
    headerStr = 'Create Support Desk Ticket';
    siteKey: string = environment.siteKey; // should pick up environment specific siteKey
    infoText =
        'The Pharmaceutical Forms System (PFS) Support provides a single source of assistance and resolution ' +
        'for any PFS-related problems or request. This includes providing useful answers regarding access, permissions, and using PFS. ' +
        'In addition, the PFS Support is used to report suspected bugs experienced while using PFS and the User Portal. ' +
        'To reach the PFS Support please submit your Support Desk Ticket request via this form or send email to ' +
        'pfssupport@doh.state.fl.us. The Support System uses the PFS Support Desk Ticketing System and will assign your email or ' +
        'phone call to a Support Desk Ticket. You will receive auto-notification back that lets you know your email has been received, ' +
        'and provides you with a Support Desk Ticket number for reference.';
    facilityDataSource = 'api/PublicPfsRegister/PfsFacilities';
    categoryDataSource = 'api/PublicPfsHelpTicket/PfsHelpCategories';
    priorityDataSource = 'api/PublicPfsHelpTicket/PfsHelpPriorities';

    isSubmitting = signal<boolean>(false);

    constructor(
        private ns: NotificationService,
        private route: ActivatedRoute,
        private recaptchaService: RecaptchaService,
        private httpClient: HttpClient,
    ) {}

    ngOnInit() {
        const queryParams = this.route?.snapshot?.queryParams;
        if (queryParams?.['id'] === 'register') {
            // The register experience is now rendered by the prettier <pcg-public-register> component, which
            // owns its own form/data/submit — nothing else to set up on this (PFS help-ticket) component.
            this.isRegister = true;
            return;
        }

        this.model.categoryId = 4;
        this.setEnv(true);

        this.httpClient.get(this.categoryDataSource).subscribe((items: SelectItem[]) => {
            this.categories = items;
            if (this.isRegister === true) {
                this.model.categoryId = items.find((o) => o.text === 'Account Registration')?.value;
                this.formGroup.controls.categoryId.setValue(this.model.categoryId);
            }
        });
        this.model.categoryName = this.categoryName;
        this.model.priorityName = this.priorityName;
        this.model.priorityId = 1;

        this.setUpHoneyPot();
    }

    setUpHoneyPot() {
        this.formGroup.controls['honeyPot'].setValidators(this.requireFalse());
        this.formGroup.updateValueAndValidity();
    }

    requireFalse(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (control.value === true) {
                return { honeyPot: true };
            }
            return null;
        };
    }

    setEnv(isPfs = true) {
        if (pcgSettings.mode === 'Development') {
            this.returnUrl = isPfs ? 'https://localhost:44384/' : 'https://localhost:44327';
        } else if (pcgSettings.mode === 'Demo') {
            this.returnUrl = isPfs ? 'https://demo.fdohcentralpharmacy.com/' : 'https://demo.fdepcs.com/';
        } else {
            this.returnUrl = isPfs ? 'https://fdohcentralpharmacy.com/' : 'https://fdepcs.com/';
        }
    }

    sanitize(email: string) {
        var strTempString = email.trim();
        strTempString = strTempString.replace("'", '');
        strTempString = strTempString.replace('--', '');
        strTempString = strTempString.replace('/', '');
        strTempString = strTempString.replace('&', '');
        strTempString = strTempString.replace('?', '');
        strTempString = strTempString.replace(':', '');
        strTempString = strTempString.replace(',', '');
        strTempString = strTempString.replace(';', '');
        strTempString = strTempString.replace('NULL', '');
        strTempString = strTempString.replace('xp_', '');
        strTempString.replace(/[\n\r]+/g, '');
        strTempString.replace('\\', '');
        this.formGroup.controls['email'].setValue(strTempString);
    }

    categoryChange() {
        this.categoryName = this.categorySelect.text;
        this.model.categoryName = this.categoryName;
        this.formGroup.controls['categoryName'].setValue(this.categoryName);
    }

    priorityChange() {
        this.priorityName = this.prioritySelect.text;
        this.model.priorityName = this.priorityName;
        this.formGroup.controls['priorityName'].setValue(this.priorityName);
    }

    facilityChange() {
        this.facilityName = this.facilitySelect.text;
        this.model.facilityName = this.facilityName;
        this.formGroup.controls['facilityName'].setValue(this.facilityName);
    }

    onSubmit() {
        if (!this.formGroup.valid) {
            validateForm(this.formGroup);
            this.isSuccess = false;
            return;
        }

        this.recaptchaService.submit(
            {
                action: 'submitPfsHelpTicket',
                apiEndpoint: 'api/PublicPfsHelpTicket/SubmitPfsHelpTicket',
                formGroup: this.formGroup,
                loadingSignal: this.isSubmitting,
            },
            {
                onSuccess: (msg: SystemMessage) => {
                    if (msg.isSuccessful) {
                        this.isSuccess = true;
                        this.formGroup.reset();
                    } else {
                        this.isSuccess = false;
                        this.ns.showError(msg.message);
                    }
                },
                onCreateTokenError: () => {
                    this.isSuccess = false;
                },
                onVerifyTokenError: () => {
                    this.isSuccess = false;
                },
            },
        );
    }
}
